import { useCallback, useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'

interface ExpansionState {
  isLandingView: boolean
  isExpanded: boolean
}

const INTERACTION_EVENTS = ['pointerdown', 'keydown'] as const

/**
 * The desktop side nav is open on the landing and closed in chat. A landing↔chat change
 * resets it; a manual collapse or expand holds until the next change.
 */
export function useSideNavExpansion(isLandingView: boolean) {
  const [state, setState] = useState<ExpansionState>({ isLandingView, isExpanded: isLandingView })
  // A prompt-link auto-send flips to chat without the traveller doing anything, and must
  // not play the collapse animation on load.
  const [hasInteracted, setHasInteracted] = useState(false)
  const menuButtonRef = useRef<HTMLButtonElement>(null)
  const collapseButtonRef = useRef<HTMLButtonElement>(null)

  let currentState = state
  if (state.isLandingView !== isLandingView) {
    currentState = { isLandingView, isExpanded: isLandingView }
    setState(currentState)
  }

  useEffect(() => {
    if (hasInteracted) return

    const handleInteraction = () => setHasInteracted(true)
    INTERACTION_EVENTS.forEach(eventName => window.addEventListener(eventName, handleInteraction, { capture: true, once: true }))
    return () => {
      INTERACTION_EVENTS.forEach(eventName => window.removeEventListener(eventName, handleInteraction, { capture: true }))
    }
  }, [hasInteracted])

  // flushSync so the target button is visible and out of `inert` before it takes focus.
  const collapse = useCallback(() => {
    flushSync(() => setState(previous => ({ ...previous, isExpanded: false })))
    menuButtonRef.current?.focus()
  }, [])

  const expand = useCallback(() => {
    flushSync(() => setState(previous => ({ ...previous, isExpanded: true })))
    collapseButtonRef.current?.focus()
  }, [])

  return {
    isExpanded: currentState.isExpanded,
    isAnimated: hasInteracted,
    menuButtonRef,
    collapseButtonRef,
    collapse,
    expand,
  }
}
